import React, { useState } from 'react';
import useImageCrop from '../hooks/useImageCrop';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, X, Camera, Loader2, PenLine } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../supabase/supabase';
import CertificateCard from '../components/common/CertificateCard';

const Signatures = () => {
  const { signatures, refresh } = useData();
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', crm: '', uf: '', image_url: '', type: 'image' });

  const isVet = user?.type === 'veterinario';
  const docLabel = isVet ? 'CRMV' : 'CRM';

  const handleOpen = (sig = null) => {
    if (sig) {
      setFormData({ name: sig.name, crm: sig.crm, uf: sig.uf, image_url: sig.image_url, type: sig.type });
      setEditingId(sig.id);
    } else {
      setFormData({ name: user.name || '', crm: user.crm || '', uf: user.uf || '', image_url: '', type: 'image' });
      setEditingId(null);
    }
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name) return alert('Nome é obrigatório');
    setLoading(true);
    try {
      if (editingId) {
        const { error } = await supabase.from('signatures').update(formData).eq('id', editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('signatures').insert([{ ...formData, user_id: user.id }]);
        if (error) throw error;
      }
      refresh();
      setIsModalOpen(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Excluir esta assinatura?')) return;
    await supabase.from('signatures').delete().eq('id', id);
    refresh();
  };

  const { cropImages, cropModal } = useImageCrop();

  const handleImageUpload = async (e) => {
    const picked = e.target.files[0];
    e.target.value = '';
    if (!picked) return;
    const cropped = await cropImages([picked]);
    if (!cropped) return;
    const [file] = cropped;
    if (!file) return;
    setLoading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/sig-${crypto.randomUUID()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('report-images').upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('report-images').getPublicUrl(fileName);
      setFormData(prev => ({ ...prev, image_url: publicUrl }));
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {cropModal}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy tracking-tight mb-2">Minhas assinaturas</h1>
          <p className="text-muted">Seus carimbos e assinaturas visuais aplicados no PDF.</p>
        </div>
        <button onClick={() => handleOpen()} className="btn-primary">
          Nova assinatura
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {signatures.map((sig) => (
          <div key={sig.id} className="bg-paper p-8 rounded-card border border-line shadow-card hover:border-navy/40 transition-colors group relative overflow-hidden">
            <div className="flex justify-end items-start mb-6">
              <div className="flex gap-2">
                <button onClick={() => handleOpen(sig)} title="Editar assinatura" aria-label={`Editar assinatura de ${sig.name}`} className="w-11 h-11 bg-paper-alt text-muted rounded-card flex items-center justify-center hover:bg-navy hover:text-white transition-colors"><PenLine className="w-4 h-4" aria-hidden="true" /></button>
                <button onClick={() => handleDelete(sig.id)} title="Excluir assinatura" aria-label={`Excluir assinatura de ${sig.name}`} className="w-11 h-11 bg-danger-tint text-danger rounded-card flex items-center justify-center hover:bg-danger hover:text-white transition-colors"><Trash2 className="w-4 h-4" aria-hidden="true" /></button>
              </div>
            </div>
            {sig.image_url && <img src={sig.image_url} alt={`Assinatura de ${sig.name}`} className="h-16 object-contain mb-6" />}
            <h3 className="font-display text-xl font-bold text-ink mb-1">{sig.name}</h3>
            <p className="text-xs text-muted font-mono">{docLabel} {sig.crm}/{sig.uf}</p>
          </div>
        ))}
        {signatures.length === 0 && (
          /* Estado vazio desenhado */
          <div className="col-span-full py-20 text-center bg-paper rounded-card border-2 border-dashed border-line">
            <h3 className="font-display text-xl font-bold text-ink mb-2">Nenhuma assinatura cadastrada</h3>
            <p className="text-muted max-w-xs mx-auto mb-6">Cadastre sua assinatura visual para que ela apareça automaticamente nos seus laudos em PDF.</p>
            <button onClick={() => handleOpen()} className="btn-primary">
              Criar minha assinatura
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 bg-navy/50 backdrop-blur-[2px] z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="bg-paper rounded-card p-8 w-full max-w-2xl shadow-xl overflow-y-auto max-h-[90vh]">
              <div className="flex justify-between items-center mb-8">
                <h2 className="font-display text-2xl font-bold text-navy tracking-tight">{editingId ? 'Editar assinatura' : 'Nova assinatura'}</h2>
                <button onClick={() => setIsModalOpen(false)} className="p-2.5 min-h-[44px] min-w-[44px] text-muted hover:text-ink transition-colors" aria-label="Fechar"><X className="w-6 h-6" aria-hidden="true" /></button>
              </div>
              <form onSubmit={handleSave} className="space-y-8">
                <div className="flex items-center gap-6">
                  <div className="relative group">
                    <div className="w-24 h-24 rounded-card bg-paper-alt border-2 border-dashed border-line flex items-center justify-center overflow-hidden cursor-pointer hover:border-navy transition-colors">
                      {formData.image_url ? <img src={formData.image_url} alt="Prévia do carimbo" className="w-full h-full object-contain" /> : <Camera className="text-muted w-8 h-8" aria-hidden="true" />}
                      <input type="file" onChange={handleImageUpload} className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" aria-label="Enviar imagem do carimbo" />
                    </div>
                    <p className="text-[10px] font-semibold text-muted uppercase tracking-widest text-center mt-2">Carimbo (Opcional)</p>
                  </div>
                  <div className="flex-1">
                    <label htmlFor="sig-name" className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">Nome do profissional</label>
                    <input id="sig-name" type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="input-field" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="sig-crm" className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">{docLabel}</label>
                    <input id="sig-crm" type="text" value={formData.crm} onChange={e => setFormData({...formData, crm: e.target.value})} className="input-field font-mono" />
                  </div>
                  <div>
                    <label htmlFor="sig-uf" className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">UF</label>
                    <input id="sig-uf" type="text" maxLength={2} value={formData.uf} onChange={e => setFormData({...formData, uf: e.target.value.toUpperCase()})} className="input-field font-mono" />
                  </div>
                </div>

                {/* Preview em tempo real sobre um mini Certificate Card — como vai sair no PDF */}
                <div>
                  <p className="text-xs font-semibold text-muted uppercase tracking-widest mb-3">Prévia no laudo</p>
                  <CertificateCard
                    variant="compact"
                    title="Laudo de exame (exemplo)"
                    patient="Paciente de Exemplo"
                    date={new Date().toLocaleDateString('pt-BR')}
                    status="registered"
                    hash="8f3a1b9e2c47d05586e1f4a7b3d9c92d"
                    footer={
                      <div className="flex items-center gap-3 pt-1">
                        {formData.image_url
                          ? <img src={formData.image_url} alt="Assinatura aplicada" className="h-10 object-contain" />
                          : <span className="text-xs text-muted italic">Sua assinatura aparecerá aqui</span>}
                        <div className="text-xs text-muted">
                          <p className="font-semibold text-ink">{formData.name || 'Nome do profissional'}</p>
                          <p className="font-mono">{docLabel} {formData.crm || '—'}/{formData.uf || '—'}</p>
                        </div>
                      </div>
                    }
                  />
                </div>

                <button type="submit" disabled={loading} className="btn-primary w-full uppercase tracking-widest text-xs">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : 'Salvar assinatura'}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Signatures;
